import React, { useState, useEffect, useRef, useCallback } from "react";
import { Send, ArrowLeft } from "lucide-react";
import cacheStorage from '../../../utils/cacheStorage';

const API_BASE = "https://smart-cargo-backend-923182991420.us-central1.run.app";

const formatMessageTime = (value) => {
    if (!value) return "";

    const normalized =
        typeof value === "string" && value.includes(" ") && !value.includes("T")
            ? value.replace(" ", "T")
            : value;
    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const createTempMessage = (senderType, content) => ({
    id: `temp-${Date.now()}-${Math.random()}`,
    sender_type: senderType,
    content,
    created_at: new Date().toISOString(),
    pending: true,
});

export function CourierMessages({ courierId, navigateTo }) {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [wsReady, setWsReady] = useState(false);

    const ws = useRef(null);
    const bottomRef = useRef(null);
    const reconnectTimer = useRef(null);

    useEffect(() => {
        const token = cacheStorage.getItem("access_token");
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };

        const qs = courierId ? `?courier_id=${courierId}` : "";

        fetch(`${API_BASE}/api/messages${qs}`, { headers })
            .then((r) => r.json())
            .then((data) => setMessages(Array.isArray(data) ? data : []))
            .catch(console.error);
    }, [courierId]);

    const connectWS = useCallback(() => {
        const token = cacheStorage.getItem("access_token");
        if (!token) return;

        const params = new URLSearchParams({ token });
        if (courierId) params.set("courier_id", courierId);

        const socket = new WebSocket(`${API_BASE.replace("http", "ws")}/api/messages?${params.toString()}`);
        socket.onopen = () => {
            setWsReady(true);
            clearTimeout(reconnectTimer.current);
        };
        socket.onclose = () => {
            setWsReady(false);
            reconnectTimer.current = setTimeout(connectWS, 3000);
        };
        socket.onerror = () => socket.close();
        socket.onmessage = (e) => {
            const msg = JSON.parse(e.data);

            setMessages((prev) => {
                const next = [...prev];

                const tempIndex = next.findIndex(
                    (message) =>
                        String(message.id).startsWith("temp-") &&
                        message.pending &&
                        message.sender_type === msg.sender_type &&
                        message.content === msg.content
                );

                if (tempIndex !== -1) {
                    next[tempIndex] = msg;
                    return next;
                }

                return next.some((message) => message.id === msg.id)
                    ? next
                    : [...next, msg];
            });
        };
        ws.current = socket;
    }, [courierId]);

    useEffect(() => {
        connectWS();

        return () => {
            clearTimeout(reconnectTimer.current);
            ws.current?.close();
        };
    }, [connectWS]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = () => {
        const text = newMessage.trim();
        if (!text || !wsReady || !ws.current) return;

        const tempMessage = createTempMessage("courier", text);

        setMessages((prev) => [...prev, tempMessage]);
        ws.current.send(JSON.stringify({ sender_type: "courier", content: text }));
        setNewMessage("");
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                background: "#f8fafc",
            }}
        >
            <div
                style={{
                    background: "#2D58AD",
                    color: "#fff",
                    padding: "1rem 1.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                }}
            >
                <button
                    onClick={() => navigateTo("dashboard")}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "#fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Messages - Dispatch</h2>
                    <span style={{ fontSize: "0.8rem", opacity: 0.8 }}>
            {wsReady ? "Connected" : "Connecting..."}
          </span>
                </div>
            </div>

            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.6rem",
                }}
            >
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        style={{
                            display: "flex",
                            justifyContent:
                                msg.sender_type === "courier" ? "flex-end" : "flex-start",
                        }}
                    >
                        <div
                            style={{
                                maxWidth: "72%",
                                padding: "0.6rem 0.9rem",
                                borderRadius: "1rem",
                                background:
                                    msg.sender_type === "courier" ? "#2D58AD" : "#ffffff",
                                color: msg.sender_type === "courier" ? "#fff" : "#1e293b",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                fontSize: "0.9rem",
                            }}
                        >
                            <p style={{ margin: 0 }}>{msg.content}</p>
                            <p
                                style={{
                                    margin: "4px 0 0",
                                    fontSize: "0.72rem",
                                    opacity: 0.65,
                                    textAlign: "right",
                                }}
                            >
                                {formatMessageTime(msg.created_at)}
                            </p>
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>

            <div
                style={{
                    display: "flex",
                    gap: "0.5rem",
                    padding: "0.75rem 1rem",
                    background: "#fff",
                    borderTop: "1px solid #e2e8f0",
                }}
            >
                <input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder={wsReady ? "Type a message..." : "Connecting..."}
                    disabled={!wsReady}
                    style={{
                        flex: 1,
                        padding: "0.6rem 1rem",
                        borderRadius: "2rem",
                        border: "1px solid #cbd5e1",
                        outline: "none",
                        fontSize: "0.9rem",
                    }}
                />
                <button
                    onClick={handleSend}
                    disabled={!wsReady}
                    style={{
                        background: "#2D58AD",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: 42,
                        height: 42,
                        cursor: wsReady ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: wsReady ? 1 : 0.5,
                    }}
                >
                    <Send size={18} />
                </button>
            </div>
        </div>
    );
}